/**
 * Bank Offers Carousel Component
 * Handles dynamic discount calculation and carousel navigation
 * Integrates with SleepyCat's variant selection system
 */

if (!customElements.get('bank-offers-carousel')) {
  customElements.define('bank-offers-carousel', class BankOffersCarousel extends HTMLElement {
    constructor() {
      super();

      // Component state
      this.currentIndex = 0;
      this.autoScrollInterval = null;
      this.offers = [];
      this.currentPrice = 0;
      this.isAutoScrolling = false;
      this.sectionId = '';

      // Event listeners cleanup
      this.eventListeners = [];
      this.variantChangeUnsubscriber = null;

      // Configuration
      this.config = {
        autoScrollSpeed: 4000, // default 4 seconds
        animationDuration: 300,
        discountCalculationDelay: 100
      };
    }

    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.config.autoScrollSpeed = (parseInt(this.dataset.autoScrollSpeed) || 4) * 1000;
      this.currentPrice = parseInt(this.dataset.currentPrice) || 0;

      this.initializeComponent();
      this.setupEventListeners();
      this.optimizeImageLoading(); // Add image optimization
      this.watchVisibility();
      this.startAutoScroll();

      // Subscribe to variant changes
      this.subscribeToVariantChanges();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    // Optimize image loading for performance
    optimizeImageLoading() {
      const images = this.querySelectorAll('.offer-image img');

      // Handle first image immediately for LCP
      if (images[0]) {
        this.loadImage(images[0], true);
      }

      // Setup intersection observer for remaining images
      if ('IntersectionObserver' in window && images.length > 1) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadImage(entry.target);
              imageObserver.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '50px', // Start loading 50px before visible
          threshold: 0.01
        });

        // Observe all images except first (already loaded)
        Array.from(images).slice(1).forEach(img => {
          imageObserver.observe(img);
        });

        this.imageObserver = imageObserver;
      } else {
        // Fallback for browsers without IntersectionObserver
        images.forEach(img => this.loadImage(img));
      }
    }

    loadImage(img, priority = false) {
      const wrapper = img.closest('.offer-image');
      if (!wrapper || img.classList.contains('loaded')) return;

      // Show loading state
      wrapper.classList.add('loading');

      // For priority images, use fetchpriority
      if (priority && 'fetchpriority' in img) {
        img.fetchpriority = 'high';
      }

      // Handle image load completion
      const handleLoad = () => {
        wrapper.classList.remove('loading');
        img.classList.add('loaded');
      };

      const handleError = () => {
        wrapper.classList.remove('loading');
        wrapper.classList.add('error');
        console.warn('Failed to load bank offer image:', img.src);
      };

      // Check if already loaded (cached)
      if (img.complete && img.naturalHeight > 0) {
        handleLoad();
      } else {
        img.addEventListener('load', handleLoad, { once: true });
        img.addEventListener('error', handleError, { once: true });

        // Force load for lazy images when needed
        if (img.loading === 'lazy' && priority) {
          img.loading = 'eager';
        }
      }
    }

    // Preload next image when navigating
    preloadNextImage() {
      const nextIndex = (this.currentIndex + 1) % this.offers.length;
      const nextImage = this.querySelectorAll('.offer-image img')[nextIndex];
      if (nextImage && !nextImage.classList.contains('loaded')) {
        this.loadImage(nextImage);
      }
    }

    initializeComponent() {
      this.collectOffers();
      this.updateOffersVisibility();
      this.updateDiscountAmounts();

      // Find first eligible offer or default to first
      const firstEligibleIndex = this.offers.findIndex(offer => offer.isEligible);
      this.currentIndex = firstEligibleIndex >= 0 ? firstEligibleIndex : 0;

      this.createDots();
      this.showCurrentOffer();
      this.updateNavigationState();
    }

    collectOffers() {
      const offerCards = this.querySelectorAll('.offer-card');
      this.offers = Array.from(offerCards).map((card, index) => ({
        element: card,
        index: index,
        discountPercent: parseFloat(card.dataset.discountPercent) || 0,
        minAmount: parseInt(card.dataset.minAmount) || 0,
        maxDiscount: parseInt(card.dataset.maxDiscount) || 0,
        isEligible: false,
        discountAmount: 0,
        link: card.dataset.offerLink || null
      }));
    }

    createDots() {
      const dotsContainer = this.querySelector('.offers-dots');
      if (!dotsContainer) return;

      dotsContainer.innerHTML = '';

      // Create dots for all offers (visible on mobile)
      this.offers.forEach((offer, index) => {
        // Only create dots for offers with content
        const hasContent = offer.element.querySelector('.offer-details');
        if (!hasContent) return;

        const dot = document.createElement('button');
        dot.className = `offer-dot ${index === this.currentIndex ? 'active' : ''}`;
        dot.setAttribute('data-index', index);
        dot.setAttribute('role', 'button');
        dot.setAttribute('tabindex', '-1'); // Not keyboard focusable on mobile
        dot.setAttribute('aria-label', `Go to offer ${index + 1}`);

        // Hide dot if offer is not eligible
        if (!offer.isEligible && index !== this.currentIndex) {
          dot.classList.add('hidden');
        }

        dot.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          if (offer.isEligible || index === this.currentIndex) {
            this.goToOffer(index);
          }
        });

        dotsContainer.appendChild(dot);
      });
    }

    setupEventListeners() {
      // Navigation buttons with arrow icons
      const prevBtn = this.querySelector('.prev-btn');
      const nextBtn = this.querySelector('.next-btn');

      if (prevBtn) {
        const prevHandler = (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.previousOffer();
        };
        prevBtn.addEventListener('click', prevHandler);
        this.eventListeners.push({ element: prevBtn, event: 'click', handler: prevHandler });
      }

      if (nextBtn) {
        const nextHandler = (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.nextOffer();
        };
        nextBtn.addEventListener('click', nextHandler);
        this.eventListeners.push({ element: nextBtn, event: 'click', handler: nextHandler });
      }

      // Pause auto-scroll on hover
      const hoverPauseHandler = () => this.pauseAutoScroll();
      const hoverResumeHandler = () => this.resumeAutoScroll();

      this.addEventListener('mouseenter', hoverPauseHandler);
      this.addEventListener('mouseleave', hoverResumeHandler);
      this.eventListeners.push({ element: this, event: 'mouseenter', handler: hoverPauseHandler });
      this.eventListeners.push({ element: this, event: 'mouseleave', handler: hoverResumeHandler });

      // Enhanced touch/swipe support
      let startX = 0;
      let startY = 0;
      let endX = 0;
      let endY = 0;
      let isDragging = false;

      const touchStartHandler = (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
        this.pauseAutoScroll();
      };

      const touchMoveHandler = (e) => {
        if (!isDragging) return;
        endX = e.touches[0].clientX;
        endY = e.touches[0].clientY;
      };

      const touchEndHandler = (e) => {
        if (!isDragging) return;
        isDragging = false;
        endX = e.changedTouches[0].clientX;
        endY = e.changedTouches[0].clientY;

        const diffX = startX - endX;
        const diffY = startY - endY;

        // Only trigger if horizontal swipe is stronger than vertical
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 30) {
          if (diffX > 0) {
            this.nextOffer();
          } else {
            this.previousOffer();
          }
        }

        setTimeout(() => this.resumeAutoScroll(), 100);
      };

      this.addEventListener('touchstart', touchStartHandler, { passive: true });
      this.addEventListener('touchmove', touchMoveHandler, { passive: true });
      this.addEventListener('touchend', touchEndHandler, { passive: true });
      this.eventListeners.push({ element: this, event: 'touchstart', handler: touchStartHandler });
      this.eventListeners.push({ element: this, event: 'touchmove', handler: touchMoveHandler });
      this.eventListeners.push({ element: this, event: 'touchend', handler: touchEndHandler });

      // Add click-to-navigate functionality for offer links
      this.setupOfferLinks();

      // Enhanced keyboard navigation
      const keyHandler = (e) => {
        if (e.target.closest('.bank-offers-carousel') === this) {
          switch (e.key) {
            case 'ArrowLeft':
              e.preventDefault();
              this.previousOffer();
              break;
            case 'ArrowRight':
              e.preventDefault();
              this.nextOffer();
              break;
            case 'Enter':
            case ' ':
              // Navigate to link if current offer has one
              const currentOffer = this.offers[this.currentIndex];
              if (currentOffer && currentOffer.link) {
                e.preventDefault();
                window.open(currentOffer.link, '_blank', 'noopener,noreferrer');
              }
              break;
          }
        }
      };

      document.addEventListener('keydown', keyHandler);
      this.eventListeners.push({ element: document, event: 'keydown', handler: keyHandler });
    }

    setupOfferLinks() {
      // Make offer cards clickable if they have links
      this.offers.forEach((offer) => {
        if (offer.link && offer.element) {
          offer.element.style.cursor = 'pointer';

          const clickHandler = (e) => {
            // Don't navigate if clicking on navigation buttons
            if (!e.target.closest('.nav-btn') && !e.target.closest('.offer-dot')) {
              e.preventDefault();
              if (window.SCAnalytics) {
                const ds = offer.element.dataset || {};
                window.SCAnalytics.push('bank_offer_click', {
                  section_name: 'bank_offers',
                  offer_name: ds.analyticsPromotionName || undefined,
                  promotion_id: ds.analyticsPromotionId || undefined,
                  offer_link: offer.link,
                  click_position: offer.index + 1,
                });
              }
              window.open(offer.link, '_blank', 'noopener,noreferrer');
            }
          };

          offer.element.addEventListener('click', clickHandler);
          this.eventListeners.push({ element: offer.element, event: 'click', handler: clickHandler });
        }
      });
    }

    subscribeToVariantChanges() {
      // Subscribe to the existing PUB_SUB_EVENTS system
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
      }

      // Fallback: Listen for price element changes
      this.observePriceChanges();
    }

    observePriceChanges() {
      const priceElement = document.querySelector(`#price-${this.sectionId}`);
      if (!priceElement) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            this.debounce(() => {
              this.updateCurrentPrice();
              this.updateComponent();
            }, this.config.discountCalculationDelay);
          }
        });
      });

      observer.observe(priceElement, {
        childList: true,
        subtree: true,
        characterData: true
      });

      this.priceObserver = observer;
    }

    handleVariantChange(event) {
      if (event && event.data && event.data.sectionId === this.sectionId) {
        this.updateCurrentPrice();
        this.updateComponent();
      }
    }

    updateCurrentPrice() {
      // Try to get price from various possible sources
      const priceSelectors = [
        `#price-${this.sectionId} .price-item--sale`,
        `#price-${this.sectionId} .price-item--regular`,
        '.price .price-item--sale',
        '.price .price-item--regular',
        '.sale_price',
        '.price__container .price-item'
      ];

      for (let selector of priceSelectors) {
        const priceElement = document.querySelector(selector);
        if (priceElement) {
          const priceText = priceElement.textContent || priceElement.innerText || '';
          const priceMatch = priceText.match(/[\d,]+/);
          if (priceMatch) {
            this.currentPrice = parseInt(priceMatch[0].replace(/,/g, '')) * 100; // Convert to paise
            break;
          }
        }
      }

      // Update data attribute
      this.dataset.currentPrice = this.currentPrice;
    }

    updateComponent() {
      this.classList.add('loading');

      this.debounce(() => {
        this.updateOffersVisibility();
        this.updateDiscountAmounts();
        this.updateActiveOffers();
        this.classList.remove('loading');
      }, this.config.discountCalculationDelay);
    }

    updateOffersVisibility() {
      let visibleOffers = 0;

      // Debug logging
      if (window.location.search.includes('debug_offers=1')) {
        console.log('[Bank Offers Debug] Current price:', this.currentPrice);
        console.log('[Bank Offers Debug] Total offers:', this.offers.length);
      }

      this.offers.forEach((offer, index) => {
        const isEligible = this.currentPrice >= offer.minAmount;
        offer.isEligible = isEligible;

        offer.element.classList.toggle('eligible', isEligible);
        offer.element.classList.toggle('ineligible', !isEligible);

        if (isEligible) {
          visibleOffers++;
        }

        // Debug logging for each offer
        if (window.location.search.includes('debug_offers=1')) {
          console.log(`[Bank Offers Debug] Offer ${index + 1}:`, {
            discountPercent: offer.discountPercent,
            minAmount: offer.minAmount,
            maxDiscount: offer.maxDiscount,
            currentPrice: this.currentPrice,
            isEligible: isEligible,
            element: offer.element
          });
        }
      });

      // Hide entire carousel if no offers are eligible
      this.classList.toggle('no-offers', visibleOffers === 0);

      if (window.location.search.includes('debug_offers=1')) {
        console.log('[Bank Offers Debug] Visible offers:', visibleOffers);
      }

      // Recreate dots when visibility changes
      this.createDots();
    }

    updateDiscountAmounts() {
      this.offers.forEach(offer => {
        if (!offer.isEligible) {
          offer.discountAmount = 0;
          return;
        }

        // Calculate exact discount amount without rounding
        const calculatedDiscount = (this.currentPrice * offer.discountPercent) / 100;
        const discountAmount = Math.min(
          calculatedDiscount,
          offer.maxDiscount * 100 // Convert to paise
        );

        offer.discountAmount = discountAmount;

        // Update description with dynamic discount
        const descriptionElement = offer.element.querySelector('.offer-description');
        if (descriptionElement) {
          const template = descriptionElement.dataset.descriptionTemplate;
          if (template && template.includes('{discount_amount}')) {
            const formattedDiscount = this.formatMoney(discountAmount);
            const updatedDescription = template.replace('{discount_amount}', formattedDiscount);
            descriptionElement.innerHTML = updatedDescription;

            // Add highlighting to discount amount
            descriptionElement.innerHTML = descriptionElement.innerHTML.replace(
              formattedDiscount,
              `<span class="discount-amount">${formattedDiscount}</span>`
            );
          }
        }

        // Add validity class
        offer.element.classList.toggle('valid-discount', discountAmount > 0);
      });
    }

    updateActiveOffers() {
      const eligibleOffers = this.offers.filter(offer => offer.isEligible);

      if (eligibleOffers.length === 0) {
        this.pauseAutoScroll();
        return;
      }

      // If current offer is not eligible, move to first eligible one
      if (!this.offers[this.currentIndex]?.isEligible) {
        this.currentIndex = eligibleOffers[0].index;
        this.showCurrentOffer();
      }

      this.resumeAutoScroll();
    }

    formatMoney(amountInPaise) {
      const amountInRupees = amountInPaise / 100;
      // Show exact amount with decimals if present
      const hasDecimals = amountInRupees % 1 !== 0;
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        const formatted = Shopify.formatMoney(amountInPaise, Shopify.money_format);
        // Keep decimals if they exist, otherwise remove .00
        return hasDecimals ? formatted : formatted.replace(/\.00$/, '');
      }
      // Format with proper decimal handling
      const options = {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2
      };
      return `₹${amountInRupees.toLocaleString('en-IN', options)}`;
    }

    // Carousel Navigation Methods
    goToOffer(index) {
      if (index < 0 || index >= this.offers.length) return;

      // Allow navigation to any offer that exists
      const offer = this.offers[index];
      if (!offer) return;

      this.currentIndex = index;
      this.showCurrentOffer();
      this.updateNavigationState();
      this.restartAutoScroll();
    }

    nextOffer() {
      const eligibleOffers = this.offers.filter((offer, idx) =>
        offer.isEligible || idx === this.currentIndex
      );
      if (eligibleOffers.length <= 1) return;

      const currentEligibleIndex = eligibleOffers.findIndex(offer => offer.index === this.currentIndex);
      const nextEligibleIndex = (currentEligibleIndex + 1) % eligibleOffers.length;
      this.goToOffer(eligibleOffers[nextEligibleIndex].index);
      this.preloadNextImage(); // Preload upcoming image
    }

    previousOffer() {
      const eligibleOffers = this.offers.filter((offer, idx) =>
        offer.isEligible || idx === this.currentIndex
      );
      if (eligibleOffers.length <= 1) return;

      const currentEligibleIndex = eligibleOffers.findIndex(offer => offer.index === this.currentIndex);
      const prevEligibleIndex = currentEligibleIndex === 0 ? eligibleOffers.length - 1 : currentEligibleIndex - 1;
      this.goToOffer(eligibleOffers[prevEligibleIndex].index);
    }

    showCurrentOffer() {
      // Debug logging
      if (window.location.search.includes('debug_offers=1')) {
        console.log('[Bank Offers Debug] Showing offer index:', this.currentIndex);
        console.log('[Bank Offers Debug] Total offers:', this.offers.length);
      }

      // Update all offers visibility
      this.offers.forEach((offer, index) => {
        const isActive = index === this.currentIndex;
        offer.element.classList.toggle('active', isActive);

        // Force display update
        if (isActive) {
          offer.element.style.display = 'block';
        } else {
          offer.element.style.display = 'none';
        }
      });

      // Update dots - show active state
      const dots = this.querySelectorAll('.offer-dot');
      dots.forEach((dot) => {
        const offerIndex = parseInt(dot.getAttribute('data-index'));
        const isActive = offerIndex === this.currentIndex;
        dot.classList.toggle('active', isActive);

        // Update visibility based on eligibility
        const offer = this.offers[offerIndex];
        if (offer) {
          const shouldShow = offer.isEligible || offerIndex === this.currentIndex;
          dot.classList.toggle('hidden', !shouldShow);
        }
      });

      // Announce to screen readers
      if (!this.isAutoAdvancing) {
        const eligibleCount = this.offers.filter(o => o.isEligible).length;
        this.announceToScreenReader(`Showing offer ${this.currentIndex + 1} of ${eligibleCount}`);
      }
    }

    updateNavigationState() {
      const eligibleOffers = this.offers.filter(offer => offer.isEligible);
      const prevBtn = this.querySelector('.prev-btn');
      const nextBtn = this.querySelector('.next-btn');

      if (prevBtn) prevBtn.disabled = eligibleOffers.length <= 1;
      if (nextBtn) nextBtn.disabled = eligibleOffers.length <= 1;
    }

    // Auto-advance only while the carousel is on screen and the tab is visible. It used to run every
    // few seconds for the whole visit (off screen too), each step toggling display and appending a
    // <div> to <body> (see announceToScreenReader), which woke every page-level MutationObserver.
    watchVisibility() {
      this.isOffscreen = false;
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          this.isOffscreen = !entry.isIntersecting;
          if (this.isOffscreen) this.pauseAutoScroll();
          else this.resumeAutoScroll();
        });
        this.visibilityObserver.observe(this);
      }
      const onVisibility = () => {
        if (document.hidden) this.pauseAutoScroll();
        else this.resumeAutoScroll();
      };
      document.addEventListener('visibilitychange', onVisibility);
      this.eventListeners.push({ element: document, event: 'visibilitychange', handler: onVisibility });
    }

    // Auto-scroll Methods
    startAutoScroll() {
      if (this.offers.filter(offer => offer.isEligible).length <= 1) return;
      if (this.isOffscreen || document.hidden) return;

      this.isAutoScrolling = true;

      this.autoScrollInterval = setInterval(() => {
        // Automatic steps are not announced (a live region that speaks every few seconds is noise
        // for screen-reader users); shopper-initiated navigation still is.
        this.isAutoAdvancing = true;
        this.nextOffer();
        this.isAutoAdvancing = false;
      }, this.config.autoScrollSpeed);
    }

    pauseAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
      this.isAutoScrolling = false;
    }

    resumeAutoScroll() {
      if (!this.isAutoScrolling && this.offers.filter(offer => offer.isEligible).length > 1) {
        this.startAutoScroll();
      }
    }

    restartAutoScroll() {
      this.pauseAutoScroll();
      setTimeout(() => this.resumeAutoScroll(), 500);
    }

    // Utility Methods
    debounce(func, wait) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(func, wait);
    }

    // One persistent live region inside the carousel (was: a new <div> appended to <body> and removed
    // a second later on every slide change).
    announceToScreenReader(message) {
      if (!this.liveRegion) {
        this.liveRegion = document.createElement('div');
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('aria-atomic', 'true');
        // .sr-only is not defined in this theme (the old <body> div was briefly VISIBLE); Dawn's class is.
        this.liveRegion.className = 'visually-hidden';
        this.appendChild(this.liveRegion);
      }
      this.liveRegion.textContent = message;
    }

    cleanup() {
      // Clear intervals
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
      }

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Unsubscribe from variant changes
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      // Remove observers
      if (this.priceObserver) {
        this.priceObserver.disconnect();
      }

      if (this.imageObserver) {
        this.imageObserver.disconnect();
      }

      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
      }

      // Remove event listeners
      this.eventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      this.eventListeners = [];

      console.log('Bank Offers Carousel cleaned up');
    }
  });
}

// Add CSS to page
if (!document.querySelector('#bank-offers-carousel-styles')) {
  // Theme assets are served from Shopify's CDN (/cdn/shop/t/<id>/assets/…), not a bare
  // "/assets/…" path — the old hardcoded href 404'd. Derive the correct asset base from an
  // already-loaded THEME asset tag. Prefer a Shopify theme-CDN tag (/cdn/shop/t/) so we
  // don't latch onto a third-party "/assets/js/widget.js" injected by an app; and only
  // rewrite when the URL actually ends in /assets/<file> (regex test) — otherwise fall
  // back to /assets/ instead of concatenating onto a non-matching foreign URL.
  var assetRef =
    document.querySelector('link[href*="/cdn/shop/t/"][href*="/assets/"], script[src*="/cdn/shop/t/"][src*="/assets/"]') ||
    document.querySelector('link[href*="/assets/"], script[src*="/assets/"]');
  var refUrl = assetRef ? (assetRef.href || assetRef.src) : '';
  var assetBase = /\/assets\/[^/]+$/.test(refUrl)
    ? refUrl.replace(/\/assets\/[^/]+$/, '/assets/')
    : '/assets/';
  const link = document.createElement('link');
  link.id = 'bank-offers-carousel-styles';
  link.rel = 'stylesheet';
  link.href = assetBase + 'component-bank-offers-carousel.css';
  document.head.appendChild(link);
}

// Initialize all carousels on page load
document.addEventListener('DOMContentLoaded', () => {
  // Component will auto-initialize when custom element is defined
  console.log('Bank Offers Carousel component ready');
});

// Handle Shopify theme editor events
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const carousels = event.target.querySelectorAll('bank-offers-carousel');
    carousels.forEach(carousel => {
      if (carousel.initializeComponent) {
        carousel.initializeComponent();
      }
    });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const carousels = event.target.querySelectorAll('bank-offers-carousel');
    carousels.forEach(carousel => {
      if (carousel.cleanup) {
        carousel.cleanup();
      }
    });
  });
}